import { useCallback } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { useAppSelector } from '@/src/store/hooks'
import { getItineraryInfo } from '@/src/store/slices/visaSlice/itineraryInfoSlice'

const useVisaNavigation = () => {
    const router = useRouter()
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const { data } = useAppSelector(getItineraryInfo)
    const { itineraryId } = data || {}

    const stepToRouteMap = {
        1: `/visa`,
        2: `/visa`,
        3: `/visa/itinerary/${itineraryId}/info`,
        4: `/visa/itinerary/${itineraryId}/info`
    }

    // Creates URL query parameters with step and optional additional parameters
    const createQueryParams = useCallback(
        (step: number, additionalParams?: Record<string, string>) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set('step', step.toString())

            if (additionalParams) {
                Object.entries(additionalParams).forEach(([key, value]) => {
                    queryParams.set(key, value)
                })
            }

            return queryParams
        },
        [searchParams]
    )

    // Navigate to a specific step in the visa application process
    const navigateToStep = useCallback(
        ({
            step,
            additionalParams,
            navigateType = 'push',
            url
        }: {
            step: number
            additionalParams?: Record<string, string>
            navigateType?: 'push' | 'replace'
            url?: string
        }) => {
            // When editing from step 4, navigate to unified page
            if (pathname.includes('/info') && step < 4) {
                router.push('/visa?step=1', { scroll: false })
                return
            }

            const route =
                url || stepToRouteMap[step as keyof typeof stepToRouteMap]
            const queryParams = createQueryParams(step, additionalParams)

            if (navigateType === 'push') {
                router.push(`${route}?${queryParams.toString()}`, {
                    scroll: false
                })
            } else {
                router.replace(`${route}?${queryParams.toString()}`, {
                    scroll: false
                })
            }
        },
        [router, createQueryParams, itineraryId, pathname]
    )

    const addStepInQuery = useCallback(
        (step: number) => {
            const queryParams = createQueryParams(step)
            router.push(`${pathname}?${queryParams.toString()}`, {
                scroll: false
            })
        },
        [router, createQueryParams, pathname]
    )

    return {
        createQueryParams,
        navigateToStep,
        addStepInQuery
    }
}

export default useVisaNavigation
